import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { Drawer } from 'antd'
import { useSelector } from 'react-redux'

import { selectTranslate } from '../../reducers/LocaleReducer/LocalReducer'
import { InstitutionContext } from '../InstitutionProvider'
import { selectActiveLanguage } from '../../reducers/LanguageReducer'
import { saveEvaluation } from '../../utils/api/evaluation'
import { displayErrorMessage, onError } from '../../utils/apiHelper'
import { HttpStatuses } from '../../utils/http'
import { selectUser } from '../../reducers/UserReducer'
import { getInternshipEvaluation } from '../../utils/api/internship'
import { InternshipEvaluation } from '../../utils/entities/internshipEvaluation'
import EvaluationPanel from '../../Components/shared/AttractivityModule/EvaluationPanels/EvaluationPanel'
import Loading from '../../HOC/Loading'

export const InstitutionFeedbackDrawerContext = createContext()

export const DEFAULT_STATE = { data: null, callback: null }

const InstitutionFeedbackDrawer = ({ children }) => {
  const { evaluationQuestions } = useContext(InstitutionContext)

  const activeLanguage = useSelector(selectActiveLanguage)
  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)

  const [handler, setHandler] = useState(DEFAULT_STATE)

  const [evaluation, setEvaluation] = useState(null)
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setSubmitted(false)
  }, [handler.data?.id])

  const language = useMemo(() => activeLanguage.code.split('_')[0], [activeLanguage])

  const loadEvaluation = useCallback(() => {
    if (user && handler.data) {
      setLoading(true)

      getInternshipEvaluation(user, handler.data).then(json => {
        if (json?.data) {
          setEvaluation(new InternshipEvaluation(json.data))
        }

        setLoading(false)
      })
    }
  }, [handler.data, user, setLoading, setEvaluation])

  const submitFeedback = (studentEvaluationAnswers, studentEvaluationComment) => {
    const questions = studentEvaluationAnswers.map(item => ({ questionId: item.id, questionType: item.questionType, answer: item.answer }))

    const dataToSend = {
      internshipId: handler.data.id,
      answers: questions,
      comment: studentEvaluationComment ? studentEvaluationComment.trim() : null
    }

    setLoading(true)
    saveEvaluation(user, dataToSend).then(json => {
      loadEvaluation()
      setSubmitted(true)
    }).catch(error => {
      if (error.status === HttpStatuses.FORBIDDEN) {
        displayErrorMessage(error, m => t(`student_feedback.${m}`))
      } else {
        onError(t('api.default_error_message'))
      }

      setLoading(false)
    })
  }

  useEffect(() => {
    loadEvaluation(user, handler.data)
  }, [loadEvaluation])

  const questions = useMemo(() => evaluationQuestions.map(q => {
    const label = q.questions.find(qq => qq.language === language)?.value ?? 'MISSING_LABEL'
    let answer = null

    if (evaluation?.studentEvaluationAnswers) {
      answer = evaluation.studentEvaluationAnswers.find(a => a.question === q.id)?.answer ?? null
    }

    return { id: q.id, label, answer }
  }), [evaluationQuestions, evaluation, language])

  const studentName = useMemo(() => {
    let name = t('Unknown student')

    if (handler.data?.student && handler.data.student.firstname) {
      name = handler.data.student.firstname
    }

    return name
  }, [handler.data?.student, t])

  return (
    <>
      <InstitutionFeedbackDrawerContext.Provider value={{ setHandler }}>
        {children}
      </InstitutionFeedbackDrawerContext.Provider>
      <Drawer
        title={t('internship_files_drawer.title')}
        width='650px'
        onClose={() => setHandler(DEFAULT_STATE)}
        visible={!!handler.data}
        zIndex={5000}
        destroyOnClose
      >
        <Loading loading={loading}>
          <EvaluationPanel
            comment={evaluation?.studentEvaluationComment ?? ''}
            questions={questions}
            name={studentName}
            submitted={submitted}
            onSubmit={submitFeedback}
          />
        </Loading>
      </Drawer>
    </>
  )
}

export default InstitutionFeedbackDrawer
